// CB:BEGIN runtime-config
// Domain-aware runtime flags for the frontend only.
// API base URL comes from getApiBaseUrl() (single source of truth).
import { getApiBaseUrl } from '../lib/getApiUrl';

export type FrontendRuntime = {
  appEnv: 'production' | 'development' | 'demo'
  demoMode: boolean
  emailDisabled: boolean
  devNavVisible: boolean
  fakeBidsEnabled: boolean
  stripeMode: 'live' | 'test'
  apiBaseUrl: string
  databaseTarget: 'production' | 'development'
  hostname: string
}

function boolFromEnv(v: string | undefined, fallback: boolean): boolean {
  if (v === 'true') return true
  if (v === 'false') return false
  return fallback
}

export function getFrontendRuntime(): FrontendRuntime {
  // Get hostname from window (SSR-safe)
  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase()
  const env = import.meta.env
  
  // Start with env vars, then override based on hostname (hostname is source of truth)
  let appEnv: 'production' | 'development' | 'demo' = (env.VITE_APP_ENV as any) || 'production'
  let demoMode = boolFromEnv(env.VITE_DEMO_MODE, false)
  let emailDisabled = boolFromEnv(env.VITE_EMAIL_DISABLED, false)
  let devNavVisible = boolFromEnv(env.VITE_DEV_NAV_VISIBLE, false)
  let fakeBidsEnabled = boolFromEnv(env.VITE_FAKE_BIDS, false)
  // CB:BEGIN stripe-key-compat
  // VITE_STRIPE_PUBLISHABLE_KEY is the canonical name; VITE_STRIPE_PUBLIC_KEY is the legacy name.
  // Prefer the canonical name so stripeMode is correct in production.
  const stripePublicKey = env.VITE_STRIPE_PUBLISHABLE_KEY || env.VITE_STRIPE_PUBLIC_KEY
  let stripeMode: 'live' | 'test' = stripePublicKey?.startsWith('pk_live_') ? 'live' : 'test'
  // CB:END stripe-key-compat
  let databaseTarget: 'production' | 'development' = 'production'
  
  // Domain-based override (per FRONTEND_ENV_GUIDE.md)
  if (host.endsWith('corebids.com')) {
    if (host === 'corebids.com' || host === 'www.corebids.com') {
      appEnv = 'production'
      demoMode = false
      emailDisabled = false
      devNavVisible = false
      fakeBidsEnabled = false
      stripeMode = 'live'
      databaseTarget = 'production'
    } else if (host.startsWith('dev.')) {
      appEnv = 'development'
      demoMode = true
      emailDisabled = true
      devNavVisible = true
      fakeBidsEnabled = true
      stripeMode = 'test'
      databaseTarget = 'development'
    } else if (host.startsWith('demo.')) {
      appEnv = 'demo'
      demoMode = true
      emailDisabled = false
      devNavVisible = false
      fakeBidsEnabled = true
      stripeMode = 'test'
      databaseTarget = 'development'
    }
  } else {
    if (!env.VITE_APP_ENV) {
      appEnv = import.meta.env.DEV ? 'development' : 'production'
    }
  }

  // Single source of truth: getApiUrl.ts (local dev → '', prod → '', dev subdomain → api.dev.corebids.com)
  const apiBaseUrl = getApiBaseUrl()
  
  return {
    appEnv,
    demoMode,
    emailDisabled,
    devNavVisible,
    fakeBidsEnabled,
    stripeMode,
    apiBaseUrl,
    databaseTarget,
    hostname: host
  }
}
// CB:END runtime-config
